import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { Can } from "components";
import ENV from "config/Env";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, LANGUAGE_CODES } from "models";
import "modules/DealersDashboard/components/CreateLocationModal/CreateLocationModal.scss";
import { useCreateLocationMutation, useLanguagesQuery } from "modules/DealersDashboard/hooks";
import { countryDropdownOptions } from "utils/common";

import type { DealerID } from "models";
import type { LocationFormPayload } from "modules/DealersDashboard/hooks";

type CreateLocationModalProps = {
  dealerId: DealerID;
  onClose: () => void;
};

const CREATE_LOCATION_FORM_ID = "create-location-form";
const FormHelper = createFormHelper<LocationFormPayload>();

const isFormInvalid = ({ name, commercial_name, country, language_code }: LocationFormPayload) => !(name && commercial_name && country && language_code);

export const CreateLocationModal = ({ dealerId, onClose }: CreateLocationModalProps) => {
  const { t } = useTranslation();

  const { data: languageOptions = [] } = useLanguagesQuery();
  const { mutate: createLocation, isPending: isCreateLocationPending } = useCreateLocationMutation();

  const formMethods = FormHelper.useForm({
    defaultValues: {
      name: "",
      active: true,
      commercial_name: "",
      headline: "",
      footerline: "",
      country: "",
      city: "",
      street: "",
      postal_code: "",
      phone_nr: "",
      language_code: LANGUAGE_CODES.English,
      logo: ""
    },
    mode: "all"
  });
  const { formState, watch } = formMethods;
  const [name, commercialName, country, languageCode] = watch(["name", "commercial_name", "country", "language_code"]);

  const handleSubmit = (values: LocationFormPayload) => {
    if (isFormInvalid(values)) return;

    createLocation({ ...values, dealer_id: dealerId }, { onSuccess: onClose });
  };

  return (
    <Modal open size="small" closeOnDimmerClick={false} onClose={onClose} className="CreateLocationModal">
      <FormHelper.FormProvider {...formMethods}>
        <Modal.Header>
          <div className="header-container">
            <div>{t("v8_create_location", "Create location")}</div>
            <FormHelper.Field.Checkbox name="active" label={t("v8_active", "Active")} />
          </div>
        </Modal.Header>

        <Modal.Content>
          <Form id={CREATE_LOCATION_FORM_ID} onSubmit={formMethods.handleSubmit(handleSubmit)} disabled={isCreateLocationPending}>
            <Form.Group>
              <Form.Field width={8} required error={formState.isSubmitted && !name}>
                <label>{t("v8_location_name", "Location Name")}</label>
                <FormHelper.Field.Input name="name" />
              </Form.Field>
              <Form.Field width={8} required error={formState.isSubmitted && !commercialName}>
                <label>{t("v8_commercial_name", "Commercial name")}</label>
                <FormHelper.Field.Input name="commercial_name" />
              </Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Field width={8}>
                <label>{t("v8_location_header", "Location header")}</label>
                <FormHelper.Field.Input name="headline" />
              </Form.Field>
              <Form.Field width={8}>
                <label>{t("v8_location_footer", "Location footer")}</label>
                <FormHelper.Field.Input name="footerline" />
              </Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Field width={8} required error={formState.isSubmitted && !country}>
                <label>{t("v8_country", "Country")}</label>
                <FormHelper.Field.Dropdown name="country" options={countryDropdownOptions} dropdownProps={{ search: true }} />
              </Form.Field>
              <Form.Field width={8}>
                <label>{t("v8_city", "City")}</label>
                <FormHelper.Field.Input name="city" />
              </Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Field width={8}>
                <label>{t("v8_street", "Street")}</label>
                <FormHelper.Field.Input name="street" />
              </Form.Field>
              <Form.Field width={8}>
                <label>{t("v8_postal_code", "Postal code")}</label>
                <FormHelper.Field.Input name="postal_code" />
              </Form.Field>
            </Form.Group>
            <Form.Group>
              <Form.Field width={8}>
                <label>{t("v8_phone", "Phone")}</label>
                <FormHelper.Field.Input name="phone_nr" />
              </Form.Field>
              <Form.Field width={8} required error={formState.isSubmitted && !languageCode}>
                <label>{t("v8_language", "Language")}</label>
                <FormHelper.Field.Dropdown name="language_code" options={languageOptions} dropdownProps={{ search: true }} />
              </Form.Field>
            </Form.Group>
            <Can I={ACL_ACTIVITIES.Update} the={ACL_RESOURCES.Logos}>
              <Form.Group>
                <Form.Field width={8}>
                  <label>{t("v8_upload_logo", "Upload Logo")} (.jpg, .jpeg, .png)</label>
                  <FormHelper.Field.UploadImage name="logo" path="/dealers/logo/upload" baseUrl={ENV.dealerBaseURL} fileFieldName="file" />
                </Form.Field>
              </Form.Group>
            </Can>
          </Form>
        </Modal.Content>

        <Modal.Actions>
          <div className="action-buttons-container">
            <Button className="light" type="button" onClick={onClose} disabled={isCreateLocationPending}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Button>
            <Button type="submit" form={CREATE_LOCATION_FORM_ID} color="green" disabled={isCreateLocationPending} loading={isCreateLocationPending}>
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Button>
          </div>
        </Modal.Actions>
      </FormHelper.FormProvider>
    </Modal>
  );
};
